import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { ExclamationTriangleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";

import { Consumable, useConsumables } from "../../api/useConsumables";
import { useConsumableSnooze } from "../../hooks/useConsumableSnooze";

/** Où recharger, par consommable. */
const RECHARGE_LINKS: Record<Consumable['key'], string> = {
    sms: '/settings/communications?tab=sms',
    email: '/settings/communications?tab=emails',
    ai: '/settings/ai',
    bot: '/settings/site-chat?tab=bot',
};

/**
 * Bandeau global d'épuisement d'un consommable.
 *
 * Ne s'affiche QUE pour le niveau `exhausted` — le service est alors
 * effectivement interrompu. Le niveau `low` est porté par la pastille du menu
 * profil : réserver le bandeau au cas grave évite la fatigue d'affichage qui
 * ferait cesser de le lire (décision de poids visuel, cf. ADR 0005).
 *
 * Et seulement pour un consommable ENGAGÉ (ADR 0007). Sur le seul solde, ce
 * bandeau partait chez 139 établissements dont 27 utilisaient réellement le
 * SMS : il inquiétait 112 hôteliers sur un canal qu'ils n'avaient jamais eu
 * l'intention d'utiliser.
 *
 * Chaque ligne peut être mise en veille 24 h. Ce qu'on ferme est l'affichage et
 * non l'alerte : l'état reste vrai, l'e-mail est déjà parti, la pastille reste
 * allumée. La veille porte UNE ligne — un hôtel à sec sur SMS et sur IA qui
 * prend acte du SMS doit encore voir l'IA.
 *
 * Le texte dit ce qui est perdu, pas seulement que le solde est vide : à zéro,
 * un SMS ne part plus du tout alors qu'un e-mail transactionnel part toujours.
 */
export default function ConsumableExhaustedBanner() {
    const { t } = useTranslation('navigation');
    const { data } = useConsumables();

    const consumables = useMemo(() => data?.consumables ?? [], [data]);

    // Avant tout `return null` : ce composant en porte un plus bas, et un hook
    // placé après ne serait pas appelé à chaque rendu.
    const { isSnoozed, snooze } = useConsumableSnooze(consumables);

    const visible = useMemo(
        () => consumables.filter(
            (item) => item.level === 'exhausted'
                && item.is_in_use
                && !isSnoozed(item.key, item.level),
        ),
        [consumables, isSnoozed],
    );

    if (visible.length === 0) {
        return null;
    }

    return (
        <div className="flex flex-col gap-2 px-4 py-3 bg-red-50 border-b border-red-200">
            {visible.map((item) => (
                <div key={item.key} className="flex items-start gap-3 text-sm">
                    <ExclamationTriangleIcon className="w-5 h-5 shrink-0 text-red-500 mt-0.5" />
                    <p className="flex-1 text-red-800">
                        <span className="font-medium">
                            {t(`consumables.exhausted_title.${item.key}`)}
                        </span>
                        {' — '}
                        <span className="text-red-700">
                            {t(`consumables.policy.${item.policy}`)}
                        </span>
                    </p>
                    <Link
                        to={RECHARGE_LINKS[item.key]}
                        className="shrink-0 px-3 py-1 rounded-md bg-red-600 text-white text-xs font-medium hover:bg-red-700 transition-colors"
                    >
                        {t(`consumables.recharge_cta.${item.key}`)}
                    </Link>
                    <button
                        type="button"
                        onClick={() => snooze(item.key, item.level)}
                        aria-label={t('consumables.dismiss')}
                        title={t('consumables.dismiss')}
                        className="shrink-0 p-1 rounded-md text-red-500 hover:bg-red-100 hover:text-red-700 transition-colors"
                    >
                        <XMarkIcon className="w-4 h-4" />
                    </button>
                </div>
            ))}
        </div>
    );
}
